<script>
  // PromptsModal — full-screen overlay for editing per-key generation prompts.
  //
  // Grouped into 3 pipelines via inner tabs (Themepack / Control Center
  // / Keyboard). Each prompt card lets the user edit + save + reset to
  // default + view the original for reference.
  //
  // PER-KEY: each ShopAIKey id has its own prompt bucket on the server.
  // The dropdown at the top picks which bucket we edit; default = the
  // tab currently active in the workspace.
  //
  // Backend: /api/admin/prompts?key_id=X (GET list / PUT update / DELETE reset).
  import { onMount } from "svelte";
  import { promptsApi } from "../api/client.js";
  import { getActiveTab } from "../stores/app.svelte.js";

  let { onClose, keys } = $props();

  let prompts = $state([]);
  let loading = $state(true);
  let error = $state(null);
  let activeTab = $state(_loadLS("tte:promptsTab", "themepack"));

  // Per-key selection. Default = active workspace tab's keyId (so the
  // modal opens on the prompts of the key the user was just looking at).
  // Persisted in LS so reload restores last viewed bucket.
  function _defaultKeyId() {
    const t = getActiveTab();
    return t?.keyId || "default";
  }
  let selectedKeyId = $state(_loadLS("tte:promptsKey", _defaultKeyId()));

  // Local UI state per prompt — busy + last-message + dirty (textarea
  // value diverges from server). Stored separately from `prompts` array
  // so re-fetching doesn't blow away in-progress edits. Resets when the
  // user switches to a different key bucket since prompts come from a
  // different file.
  let localState = $state({});  // { [key]: { value, busy, msg, msgKind, dirty } }

  function _loadLS(key, fallback) {
    if (typeof localStorage === "undefined") return fallback;
    try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
  }
  function _saveLS(key, value) {
    if (typeof localStorage === "undefined") return;
    try { localStorage.setItem(key, value); } catch { /* */ }
  }
  $effect(() => { _saveLS("tte:promptsTab", activeTab); });
  $effect(() => { _saveLS("tte:promptsKey", selectedKeyId); });

  // Build the key picker options: default bucket + every stored key. The
  // default bucket is what jobs running on the .env fallback key read.
  const keyOptions = $derived([
    { id: "default", label: "Default (.env / fallback)" },
    ...(keys || []).map(k => ({
      id: k.id,
      label: `${k.label} · ${k.masked_key}`,
    })),
  ]);

  // Pipeline grouping mirrors the legacy drawer + adds the new
  // "CC Preview" tab for the single-shot themed mockup prompt.
  function tabOf(key) {
    if (key === "cc_preview") return "cc_preview";
    if (key.startsWith("cc_")) return "control_center";
    if (key.startsWith("kb_")) return "keyboard";
    return "themepack";
  }

  // Human-readable labels for each prompt key.
  const HEADINGS = {
    background:      "Background (themepack wallpaper)",
    icons_sheet_1:   "Icons sheet 1/2 (brands 0-17, 18 icons — page 1)",
    icons_sheet_2:   "Icons sheet 2/2 (brands 18-35, 18 icons — references sheet 1 as style anchor)",
    widgets_sheet:   "Widgets sheet (themepack 4 widgets)",
    cc_background:   "CC Background (Control Center wallpaper)",
    cc_surfaces:     "CC Surfaces (7 widget bg tiles)",
    cc_pills_decor:  "CC Pills + Decor (10 items)",
    cc_toggle_icons: "CC Toggle Icons (21 icons)",
    cc_preview:      "CC Preview (single-shot 1170×2532 mockup — Zone C = 4 themed image vignettes, không phải letters)",
    kb_background:   "KB Backdrop (750×488 themed wallpaper)",
    kb_sheet:        "KB Key Sheet (16 keys, inpainted)",
  };

  async function loadPrompts() {
    loading = true;
    error = null;
    try {
      const data = await promptsApi.list(selectedKeyId);
      prompts = data.prompts || [];
      // Initialize local state for each prompt with the server's current.
      for (const p of prompts) {
        if (!localState[p.key]) {
          localState[p.key] = { value: p.current, busy: false, msg: "", msgKind: "", dirty: false };
        } else {
          // If the local copy is clean (matches what the server had last
          // time), bring it up to the latest current. If dirty, leave it
          // alone so the user doesn't lose unsaved edits.
          if (!localState[p.key].dirty) {
            localState[p.key].value = p.current;
          }
        }
      }
    } catch (err) {
      error = err.message;
    } finally {
      loading = false;
    }
  }

  // Re-fetch whenever the user switches buckets. Wipe localState so
  // textareas reflect the new bucket's values (not the previous one's
  // unsaved edits).
  function onKeyChange(e) {
    selectedKeyId = e.target.value;
    localState = {};
    loadPrompts();
  }

  onMount(loadPrompts);

  async function savePrompt(key) {
    const ls = localState[key];
    if (!ls || ls.busy) return;
    ls.busy = true;
    ls.msg = "Đang lưu...";
    ls.msgKind = "muted";
    try {
      const updated = await promptsApi.update(key, ls.value, selectedKeyId);
      // Patch the prompts array with the updated entry.
      const idx = prompts.findIndex((p) => p.key === key);
      if (idx !== -1) prompts[idx] = updated;
      ls.dirty = false;
      ls.msg = "✓ Đã lưu";
      ls.msgKind = "ok";
    } catch (err) {
      ls.msg = `Lỗi: ${err.message}`;
      ls.msgKind = "error";
    } finally {
      ls.busy = false;
    }
  }

  async function resetPrompt(key) {
    const p = prompts.find((x) => x.key === key);
    if (!p) return;
    if (!confirm(`Reset prompt "${HEADINGS[key] || key}" về mặc định?\n\nKhông thể undo.`)) return;
    const ls = localState[key];
    ls.busy = true;
    ls.msg = "Đang reset...";
    ls.msgKind = "muted";
    try {
      const updated = await promptsApi.reset(key, selectedKeyId);
      const idx = prompts.findIndex((x) => x.key === key);
      if (idx !== -1) prompts[idx] = updated;
      ls.value = updated.current;
      ls.dirty = false;
      ls.msg = "✓ Đã reset";
      ls.msgKind = "ok";
    } catch (err) {
      ls.msg = `Lỗi: ${err.message}`;
      ls.msgKind = "error";
    } finally {
      ls.busy = false;
    }
  }

  function onTextareaInput(key, e) {
    const ls = localState[key];
    if (!ls) return;
    ls.value = e.target.value;
    // Dirty = textarea diverges from server's current value.
    const p = prompts.find((x) => x.key === key);
    ls.dirty = p ? (ls.value !== p.current) : true;
  }

  // Reactive: which prompts belong to the active tab + their counts.
  const visiblePrompts = $derived(
    prompts.filter((p) => tabOf(p.key) === activeTab)
  );
  const counts = $derived(
    prompts.reduce((acc, p) => {
      const t = tabOf(p.key);
      acc[t] = acc[t] || { total: 0, overridden: 0 };
      acc[t].total++;
      if (p.is_overridden) acc[t].overridden++;
      return acc;
    }, {})
  );

  // Close on Esc + outside click.
  function onKeydown(e) {
    if (e.key === "Escape") onClose?.();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="modal-overlay" role="presentation"
     onclick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
  <div class="modal-panel">
    <header class="modal-head">
      <h2>📝 Edit prompts</h2>
      <div class="head-controls">
        <label class="key-picker">
          <span class="muted">Key bucket:</span>
          <select value={selectedKeyId} onchange={onKeyChange} disabled={loading}>
            {#each keyOptions as opt (opt.id)}
              <option value={opt.id}>{opt.label}</option>
            {/each}
          </select>
        </label>
        <button class="link-btn" onclick={() => onClose?.()}>Đóng (Esc)</button>
      </div>
    </header>

    <!-- Pipeline tabs -->
    <div class="tabs">
      {#each [
        ["themepack",       "🎨 Themepack"],
        ["control_center",  "🎛️ Control Center"],
        ["cc_preview",      "🎬 CC Preview"],
        ["keyboard",        "⌨️ Keyboard"],
      ] as [id, label]}
        <button class:active={activeTab === id} onclick={() => (activeTab = id)}>
          {label}
          {#if counts[id]}
            <span class="tab-count">
              {#if counts[id].overridden > 0}
                ({counts[id].overridden}/{counts[id].total} đã sửa)
              {:else}
                ({counts[id].total})
              {/if}
            </span>
          {/if}
        </button>
      {/each}
    </div>

    <!-- Body -->
    <div class="modal-body">
      {#if loading}
        <p class="muted">Đang tải prompts...</p>
      {:else if error}
        <div class="error">Lỗi: {error}</div>
      {:else}
        {#each visiblePrompts as p (p.key)}
          <article class="prompt-block">
            <header>
              <h3>
                {HEADINGS[p.key] || p.key}
                {#if p.is_overridden}
                  <span class="badge badge-overridden">Đã sửa</span>
                {/if}
                {#if localState[p.key]?.dirty}
                  <span class="badge badge-dirty">Chưa lưu</span>
                {/if}
              </h3>
              {#if p.placeholders?.length > 0}
                <span class="placeholders muted">
                  Placeholders: {p.placeholders.map((x) => `{${x}}`).join(", ")}
                </span>
              {/if}
            </header>

            <textarea rows="14" spellcheck="false"
                      value={localState[p.key]?.value ?? p.current}
                      oninput={(e) => onTextareaInput(p.key, e)}
                      disabled={localState[p.key]?.busy}></textarea>

            <details class="default-view">
              <summary>Xem default</summary>
              <pre>{p.default}</pre>
            </details>

            {#if localState[p.key]?.msg}
              <p class="msg msg-{localState[p.key].msgKind}">{localState[p.key].msg}</p>
            {/if}

            <div class="actions">
              <button class="primary-btn"
                      onclick={() => savePrompt(p.key)}
                      disabled={localState[p.key]?.busy || !localState[p.key]?.dirty}>
                {localState[p.key]?.busy ? "..." : "Save"}
              </button>
              <button class="link-btn"
                      onclick={() => resetPrompt(p.key)}
                      disabled={localState[p.key]?.busy || !p.is_overridden}>
                Reset to default
              </button>
            </div>
          </article>
        {/each}
      {/if}
    </div>
  </div>
</div>

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(2px);
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  .modal-panel {
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    width: min(1000px, 96vw);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: var(--shadow-md);
  }

  .modal-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 20px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-elevated);
    flex-shrink: 0;
  }

  .modal-head h2 {
    margin: 0;
    font-size: 1.05rem;
    color: var(--text-strong);
  }

  .head-controls {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .key-picker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.82rem;
  }
  .key-picker select {
    padding: 4px 8px;
    background: var(--panel);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 4px;
    font-size: 0.82rem;
    max-width: 260px;
  }

  /* Pipeline tabs */
  .tabs {
    display: flex;
    gap: 4px;
    padding: 12px 20px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-elevated);
    flex-shrink: 0;
    flex-wrap: wrap;
  }
  .tabs button {
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 0.85rem;
    color: var(--muted);
    font-weight: 500;
    transition: background 0.15s, color 0.15s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .tabs button:hover:not(.active) {
    background: rgba(255, 255, 255, 0.04);
    color: var(--text);
  }
  .tabs button.active {
    color: var(--text-strong);
    background: var(--panel-2);
    font-weight: 600;
  }
  .tab-count {
    font-size: 0.72rem;
    opacity: 0.7;
    font-weight: 400;
  }

  /* Body */
  .modal-body {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    min-height: 0;
  }

  .muted { color: var(--muted); font-size: 0.85rem; }
  .error {
    padding: 12px 14px;
    background: rgba(248, 113, 113, 0.08);
    border: 1px solid rgba(248, 113, 113, 0.3);
    border-radius: 6px;
    color: #ff8585;
    font-size: 0.82rem;
  }

  /* Prompt block */
  .prompt-block {
    background: var(--panel-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
  }
  .prompt-block header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
  }
  .prompt-block h3 {
    margin: 0;
    font-size: 0.92rem;
    color: var(--text-strong);
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .placeholders {
    font-size: 0.7rem;
    font-family: ui-monospace, monospace;
  }

  .badge {
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .badge-overridden {
    background: rgba(251, 191, 36, 0.15);
    color: #ffd067;
  }
  .badge-dirty {
    background: rgba(124, 158, 255, 0.15);
    color: var(--accent);
  }

  textarea {
    width: 100%;
    min-height: 220px;
    resize: vertical;
    font-family: ui-monospace, monospace;
    font-size: 0.78rem;
    line-height: 1.5;
    padding: 10px;
    background: var(--bg);
  }

  .default-view summary {
    cursor: pointer;
    font-size: 0.78rem;
    color: var(--accent);
  }
  .default-view summary:hover { color: var(--accent-strong); }
  .default-view pre {
    white-space: pre-wrap;
    font-family: ui-monospace, monospace;
    font-size: 0.72rem;
    line-height: 1.5;
    max-height: 320px;
    overflow: auto;
    background: var(--bg);
    border: 1px solid var(--border);
    padding: 10px 12px;
    border-radius: var(--radius-sm);
    margin: 8px 0 0;
  }

  .msg {
    margin: 0;
    font-size: 0.78rem;
  }
  .msg-ok    { color: #4cd197; }
  .msg-error { color: #ff8585; }
  .msg-muted { color: var(--muted); }

  .actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }
</style>
